import { useCallback, useRef, MutableRefObject } from "react";
import { AuthInterceptor } from "@/auth/AuthInterceptor";
import * as auth from "@/services/auth";
import { NetworkDetector } from "@/services/networkDetector";
import { fetchAccount } from "@/modules/account/service";
import { Account } from "@/types/account";
import * as Sentry from "@sentry/react";

const getTokenFingerprint = (token: string): string =>
  token ? `${token.slice(0, 12)}...${token.slice(-8)}` : "(none)";

interface UseSessionManagementParams {
  impersonateCognitoIdRef: MutableRefObject<string | null>;
  setAccount: (account: Account | null) => void;
}

export const useSessionManagement = ({
  impersonateCognitoIdRef,
  setAccount,
}: UseSessionManagementParams) => {
  const refAuthInterceptor = useRef<(() => void) | null>(null);

  const destroyAuthInterceptor = useCallback(() => {
    if (refAuthInterceptor.current) {
      refAuthInterceptor.current();
      refAuthInterceptor.current = null;
    }
  }, []);

  const destroyAccount = useCallback(() => {
    setAccount(null);
  }, [setAccount]);

  const destroySession = useCallback(async () => {
    NetworkDetector.stop();
    destroyAuthInterceptor();
    destroyAccount();
  }, [destroyAuthInterceptor, destroyAccount]);

  const initializeAuthInterceptor = useCallback(async () => {
    if (!auth.isSignedIn()) {
      Sentry.captureMessage(
        "[AR-1810] useSessionManagement: initializeAuthInterceptor — NOT SIGNED IN",
        { level: "info", tags: { ar1810: true } },
      );
      return;
    }
    Sentry.captureMessage(
      "[AR-1810] useSessionManagement: initializeAuthInterceptor — START",
      { level: "info", tags: { ar1810: true } },
    );
    try {
      const idToken = await auth.getIdToken();
      if (idToken && refAuthInterceptor.current === null) {
        Sentry.captureMessage(
          `[AR-1810] useSessionManagement: initializeAuthInterceptor — creating interceptor (token: ${getTokenFingerprint(idToken)})`,
          { level: "info", tags: { ar1810: true } },
        );
        refAuthInterceptor.current = AuthInterceptor(
          idToken,
          () => impersonateCognitoIdRef.current,
        );
      } else if (refAuthInterceptor.current !== null) {
        Sentry.captureMessage(
          "[AR-1810] useSessionManagement: initializeAuthInterceptor — interceptor already exists, skipping",
          { level: "info", tags: { ar1810: true } },
        );
      }
    } catch (err) {
      Sentry.captureMessage(
        "[AR-1810] useSessionManagement: initializeAuthInterceptor — FAILED",
        {
          level: "error",
          tags: { ar1810: true },
          extra: { error: (err as Error).message },
        },
      );
      throw err;
    }
  }, [impersonateCognitoIdRef]);

  const initializeAccount = useCallback(async () => {
    try {
      const signedInAccount = await fetchAccount();
      if (signedInAccount !== undefined) {
        setAccount(signedInAccount);
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, [setAccount]);

  const initializeSession = useCallback(async () => {
    await initializeAuthInterceptor();
    await initializeAccount();
    NetworkDetector.start();
  }, [initializeAuthInterceptor, initializeAccount]);

  return {
    destroySession,
    initializeSession,
  };
};
